import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Check, Code, Copy, ExternalLink, Monitor, Smartphone } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/layout/Card'
import Select from '@/components/ui/Select'
import FormView, { type FormValues } from '@/features/forms/FormView'
import FieldsEditor from '@/features/forms/FieldsEditor'
import FormSubmissionsTable from '@/features/forms/FormSubmissionsTable'
import AfterSubmitSteps, { type AfterSubmitValue } from '@/features/forms/AfterSubmitSteps'
import { useCreateForm, useForm, useForms, useUpdateForm } from '@/features/forms/useForms'
import { DEFAULT_FIELDS, OPTION_DATA_TYPES, copyText, embedSnippet, stagesOf } from '@/features/forms/formBuilder'
import { useMembers } from '@/features/team/useTeam'
import { usePipelineDetail, usePipelines } from '@/features/board/useBoard'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/features/auth/authStore'
import type { Form, FormField, FormInput, FormStyle } from '@/api/forms'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Everything the builder edits, as one value — dirty-checking and the preview read it whole. */
interface Draft {
  name: string
  description: string
  enabled: boolean
  fields: FormField[]
  hideTitle: boolean
  createDeal: boolean
  ownerUserId: string
  dealPipelineId: string | null
  dealStageId: string | null
  dealNameTemplate: string
  afterSubmit: 'message' | 'redirect'
  thankYouMessage: string
  redirectUrl: string
  notifyEmails: string
  style: FormStyle
  submitLabel: string
  after: AfterSubmitValue
  listingDealId: string | null
}

function draftOf(f?: Form): Draft {
  return {
    name: f?.name ?? '',
    description: f?.description ?? '',
    enabled: f?.enabled ?? true,
    fields: f ? f.fields.map((x) => ({ ...x })) : DEFAULT_FIELDS.map((x) => ({ ...x })),
    hideTitle: f?.hideTitle ?? false,
    createDeal: f?.createDeal ?? true,
    ownerUserId: f?.ownerUserId ?? '',
    dealPipelineId: f?.dealPipelineId ?? null,
    dealStageId: f?.dealStageId ?? null,
    dealNameTemplate: f?.dealNameTemplate ?? '',
    afterSubmit: f?.redirectUrl ? 'redirect' : 'message',
    thankYouMessage: f?.thankYouMessage ?? '',
    redirectUrl: f?.redirectUrl ?? '',
    notifyEmails: (f?.notifyEmails ?? []).join(', '),
    style: { ...(f?.style ?? {}) },
    submitLabel: f?.submitLabel ?? '',
    after: {
      addToListId: f?.addToListId ?? '',
      autoReplyTemplateId: f?.autoReplyTemplateId ?? '',
      enrolSequenceId: f?.enrolSequenceId ?? '',
    },
    listingDealId: f?.listingDealId ?? null,
  }
}

/** Colour presets for the Appearance tab; empty = the agency's own brand colours. */
const PRESETS: { name: string; style: FormStyle }[] = [
  { name: 'Brand', style: {} },
  { name: 'Slate', style: { background: '#FFFFFF', text: '#1F2A30', accent: '#4E6772', buttonText: '#FFFFFF' } },
  { name: 'Warm', style: { background: '#F7F3EE', text: '#2D2A26', accent: '#8C6A4F', buttonText: '#FFFFFF' } },
  { name: 'Dark', style: { background: '#16202A', text: '#F2F5F7', accent: '#E5B85C', buttonText: '#16202A' } },
]

const COLOR_SLOTS: { key: keyof FormStyle; label: string }[] = [
  { key: 'background', label: 'Background' },
  { key: 'text', label: 'Text' },
  { key: 'accent', label: 'Button' },
  { key: 'buttonText', label: 'Button text' },
]

type Tab = 'fields' | 'routing' | 'after' | 'appearance' | 'share' | 'submissions'

function Labeled({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/**
 * One form, built on its own page: settings in tabs on the left, and on the right a live preview of
 * exactly what visitors will see (desktop or phone width) — so it's always clear which form is being
 * edited and what it will look like. Share (link + embed code) and the submissions live here too.
 */
export default function FormEditorPage() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  // Agents see the forms whose leads go to them, read-only: who filled it in, and the link to share.
  const readOnly = useAuthStore((st) => st.user?.role !== 'AGENCY_ADMIN' && st.user?.role !== 'PLATFORM_ADMIN')
  const requestedTab = (params.get('tab') as Tab) || 'fields'
  const tab: Tab = readOnly && requestedTab !== 'share' ? 'submissions' : requestedTab
  const setTab = (t: Tab) => setParams(t === 'fields' ? {} : { tab: t }, { replace: true })

  const { data: form, isError } = useForm(id)
  const { data: forms } = useForms()
  const { data: members } = useMembers()
  const { data: pipelines } = usePipelines()
  const create = useCreateForm()
  const update = useUpdateForm()
  const save = isNew ? create : update

  const [draft, setDraft] = useState<Draft>(() => draftOf())
  const [baseline, setBaseline] = useState<string>(() => JSON.stringify(draftOf()))
  const [loadedId, setLoadedId] = useState<string | null>(null)
  // load the saved form into the draft once it arrives (and when switching forms)
  if (form && form.id !== loadedId) {
    setLoadedId(form.id)
    setDraft(draftOf(form))
    setBaseline(JSON.stringify(draftOf(form)))
  }
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const dirty = JSON.stringify(draft) !== baseline

  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop')
  const [previewValues, setPreviewValues] = useState<FormValues>({})
  const [previewNote, setPreviewNote] = useState(false)
  const [copied, setCopied] = useState<'link' | 'embed' | null>(null)

  const defaultPipelineId = pipelines?.find((p) => p.isDefault)?.id ?? pipelines?.[0]?.id ?? null
  const pipelineId = draft.dealPipelineId ?? defaultPipelineId
  const { data: pipelineDetail } = usePipelineDetail(draft.createDeal ? pipelineId ?? undefined : undefined)
  const stages = stagesOf(pipelineDetail)

  const cleanFields = useMemo(
    () =>
      draft.fields
        .map((f) => ({
          ...f,
          key: f.key.trim(),
          label: f.label.trim(),
          options: OPTION_DATA_TYPES.includes(f.type) ? (f.options ?? []).map((o) => o.trim()).filter(Boolean) : undefined,
          value: f.hidden ? (f.value ?? '').trim() : undefined,
        }))
        .filter((f) => f.key && f.label),
    [draft.fields],
  )
  const choiceNeedsOptions = cleanFields.some((f) => OPTION_DATA_TYPES.includes(f.type) && (f.options ?? []).length === 0)
  // A field mapped to a Deal field only lands on a deal when the form creates one — otherwise its answer
  // is silently dropped. Block save so a form can't ship quietly losing data.
  const dealFieldNeedsDeal = !draft.createDeal && cleanFields.some((f) => !!f.custom && f.entity === 'deal')
  const duplicateName =
    !!draft.name.trim() && (forms ?? []).some((f) => f.id !== id && f.name.trim().toLowerCase() === draft.name.trim().toLowerCase())
  const problems = [
    !draft.name.trim() && 'Give the form a name.',
    cleanFields.length === 0 && 'Add at least one field.',
    choiceNeedsOptions && 'Choice fields need at least one option.',
    cleanFields.some((f) => f.hidden && !f.value) && 'A hidden field needs the value it always saves.',
    dealFieldNeedsDeal && 'A field saves to a deal — turn on “Create a deal” (Lead routing) or change where it is saved.',
  ].filter(Boolean) as string[]

  // warn before closing the tab with unsaved changes
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  function submit() {
    if (problems.length) return
    const input: FormInput = {
      name: draft.name.trim(),
      enabled: draft.enabled,
      fields: cleanFields,
      createDeal: draft.createDeal,
      ownerUserId: draft.ownerUserId || null,
      dealPipelineId: draft.createDeal ? pipelineId : null,
      dealStageId: draft.createDeal ? draft.dealStageId : null,
      dealNameTemplate: draft.createDeal ? draft.dealNameTemplate.trim() || null : null,
      description: draft.description.trim() || null,
      thankYouMessage: draft.afterSubmit === 'message' ? draft.thankYouMessage.trim() || null : null,
      redirectUrl: draft.afterSubmit === 'redirect' ? draft.redirectUrl.trim() || null : null,
      notifyEmails: draft.notifyEmails.split(/[,\n;]/).map((e) => e.trim()).filter(Boolean),
      hideTitle: draft.hideTitle,
      style: draft.style,
      submitLabel: draft.submitLabel.trim() || null,
      addToListId: draft.after.addToListId || null,
      autoReplyTemplateId: draft.after.autoReplyTemplateId || null,
      enrolSequenceId: draft.after.enrolSequenceId || null,
      listingDealId: draft.listingDealId,
    }
    if (isNew) {
      create.mutate(input, { onSuccess: (f) => { setBaseline(JSON.stringify(draft)); navigate(`/forms/${f.id}`, { replace: true }) } })
    } else if (id) {
      update.mutate({ id, input }, { onSuccess: (f) => { setDraft(draftOf(f)); setBaseline(JSON.stringify(draftOf(f))) } })
    }
  }

  async function copy(kind: 'link' | 'embed') {
    if (!id) return
    const text = kind === 'link' ? `${window.location.origin}/f/${id}` : embedSnippet(id, draft.name)
    if (await copyText(text)) {
      setCopied(kind)
      setTimeout(() => setCopied(null), 1500)
    }
  }

  if (isError) {
    return (
      <PageContainer width="full">
        <p className="text-sm text-gray-500">
          This form doesn't exist or was deleted. <Link to="/forms" className="text-brand-primary hover:underline">Back to forms</Link>
        </p>
      </PageContainer>
    )
  }
  if (!isNew && !form) return <PageContainer width="full"><div className="h-40 rounded-xl bg-gray-100 animate-pulse" /></PageContainer>

  const publicUrl = id ? `${window.location.origin}/f/${id}` : null
  const TABS: { id: Tab; label: string; hidden?: boolean }[] = [
    { id: 'fields', label: 'Fields', hidden: readOnly },
    { id: 'routing', label: 'Lead routing', hidden: readOnly },
    { id: 'after', label: 'After submit', hidden: readOnly },
    { id: 'appearance', label: 'Appearance', hidden: readOnly },
    { id: 'share', label: 'Share & embed', hidden: isNew },
    { id: 'submissions', label: `Submissions${form?.submissionCount ? ` · ${form.submissionCount}` : ''}`, hidden: isNew },
  ]

  return (
    <PageContainer width="full">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
        <Link to="/forms" className="hover:text-brand-primary">Forms</Link>
        <span aria-hidden>/</span>
        <span className="text-gray-700 truncate">{isNew ? 'New form' : form?.name}</span>
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {readOnly ? (
          <h1 className="flex-1 min-w-64 text-2xl font-semibold text-brand-darkest">{form?.name}</h1>
        ) : (
          <input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Form name — e.g. Off-market enquiry"
            aria-label="Form name"
            className="flex-1 min-w-64 max-w-xl text-2xl font-semibold text-brand-darkest bg-transparent border-b border-transparent hover:border-gray-200 focus:border-brand-primary focus:outline-none px-0 py-1"
          />
        )}
        {readOnly ? (
          <div className="flex items-center gap-2">
            <span className={cn('inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium', form?.enabled ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500')}>
              <span className={cn('w-2 h-2 rounded-full', form?.enabled ? 'bg-success' : 'bg-gray-300')} />
              {form?.enabled ? 'Live' : 'Paused'}
            </span>
            {publicUrl && form?.enabled && (
              <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">
                <ExternalLink size={15} /> Open
              </a>
            )}
            <span className="text-xs text-gray-400">Leads from this form come to you. Your admin manages the form itself.</span>
          </div>
        ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={draft.enabled}
            onClick={() => set('enabled', !draft.enabled)}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium',
              draft.enabled ? 'border-success/30 bg-success/10 text-success' : 'border-gray-200 text-gray-500',
            )}
            title={draft.enabled ? 'Accepting submissions — click to pause' : 'Paused — the public page shows "not available"'}
          >
            <span className={cn('w-2 h-2 rounded-full', draft.enabled ? 'bg-success' : 'bg-gray-300')} />
            {draft.enabled ? 'Live' : 'Paused'}
          </button>
          {publicUrl && form?.enabled && (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50">
              <ExternalLink size={15} /> Open
            </a>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={save.isPending || problems.length > 0 || (!isNew && !dirty)}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : isNew ? 'Create form' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
        )}
      </div>
      {duplicateName && <p className="text-xs text-amber-700 -mt-2 mb-3">Another form already has this name — a different one avoids mix-ups.</p>}
      {save.isError && <p className="text-danger text-sm mb-3">{apiErrorMessage(save.error)}</p>}
      {!readOnly && problems.length > 0 && (isNew ? draft.name.trim() !== '' : true) && (
        <ul className="mb-3 text-xs text-amber-700 space-y-0.5">
          {problems.map((p) => <li key={p}>• {p}</li>)}
        </ul>
      )}

      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto">
        {TABS.filter((t) => !t.hidden).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-2 text-sm whitespace-nowrap -mb-px border-b-2',
              tab === t.id ? 'border-brand-primary text-brand-primary font-medium' : 'border-transparent text-gray-500 hover:text-brand-dark',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'submissions' && form ? (
        <Card title="Submissions">
          <FormSubmissionsTable form={form} />
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
          <div className="min-w-0 space-y-4">
            {tab === 'fields' && (
              <>
                <Labeled label="Description" hint="Shown under the title on the form.">
                  <textarea className={inputCls} rows={2} value={draft.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. Fill in the form to get more information." />
                </Labeled>
                <FieldsEditor fields={draft.fields} onChange={(f) => set('fields', f)} createDeal={draft.createDeal} />
              </>
            )}

            {tab === 'routing' && (
              <Card title="Who gets the lead">
                <div className="space-y-4">
                  <Labeled label="Assign new leads to" hint="Each submission creates (or updates) a contact owned by this person.">
                    <Select size="md" value={draft.ownerUserId} onChange={(e) => set('ownerUserId', e.target.value)} className="w-full">
                      <option value="">Unassigned</option>
                      {(members ?? []).map((m) => <option key={m.id} value={m.id}>{m.fullName}</option>)}
                    </Select>
                  </Labeled>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={draft.createDeal} onChange={(e) => set('createDeal', e.target.checked)} />
                    Create a deal for every submission
                  </label>
                  {draft.createDeal && (
                    <div className="grid gap-3 sm:grid-cols-2 pl-6">
                      <Labeled label="Pipeline">
                        <Select size="md" value={pipelineId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, dealPipelineId: e.target.value || null, dealStageId: null }))} className="w-full">
                          {(pipelines ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>)}
                        </Select>
                      </Labeled>
                      <Labeled label="Stage">
                        <Select size="md" value={draft.dealStageId ?? ''} onChange={(e) => set('dealStageId', e.target.value || null)} className="w-full">
                          <option value="">First stage</option>
                          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                      </Labeled>
                      <div className="sm:col-span-2">
                        <Labeled label="Deal name" hint="Use {{field_key}} from the fields, e.g. {{first_name}} {{last_name}} — web lead. Blank = the default name.">
                          <input className={inputCls} value={draft.dealNameTemplate} onChange={(e) => set('dealNameTemplate', e.target.value)} placeholder="{{first_name}} {{last_name}} — web lead" />
                        </Labeled>
                      </div>
                    </div>
                  )}
                  <Labeled label="Email these people about each submission" hint="Comma or line separated. Replies go straight to the lead when they gave an email.">
                    <textarea className={inputCls} rows={2} value={draft.notifyEmails} onChange={(e) => set('notifyEmails', e.target.value)} placeholder="sales@agency.com.au, jane@agency.com.au" />
                  </Labeled>
                </div>
              </Card>
            )}

            {tab === 'after' && (
              <Card title="After someone submits">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="radio" name="after" checked={draft.afterSubmit === 'message'} onChange={() => set('afterSubmit', 'message')} />
                    Show a thank-you message
                  </label>
                  {draft.afterSubmit === 'message' && (
                    <textarea className={inputCls} rows={2} value={draft.thankYouMessage} onChange={(e) => set('thankYouMessage', e.target.value)} placeholder="We've received your details and will be in touch shortly." />
                  )}
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="radio" name="after" checked={draft.afterSubmit === 'redirect'} onChange={() => set('afterSubmit', 'redirect')} />
                    Send them to a page on your website
                  </label>
                  {draft.afterSubmit === 'redirect' && (
                    <Labeled label="Page address" hint="The whole page goes there — even when the form is embedded. Useful for ad pixels on your own thank-you page.">
                      <input className={inputCls} value={draft.redirectUrl} onChange={(e) => set('redirectUrl', e.target.value)} placeholder="https://www.calibrerealestate.com.au/thank-you" />
                    </Labeled>
                  )}
                </div>
              </Card>
            )}
            {tab === 'after' && <AfterSubmitSteps value={draft.after} onChange={(v) => set('after', v)} />}

            {tab === 'appearance' && (
              <Card title="Look and feel">
                <div className="space-y-5">
                  <div>
                    <span className="block text-xs font-medium text-gray-500 mb-2">Colour presets</span>
                    <div className="flex flex-wrap gap-2">
                      {PRESETS.map((p) => (
                        <button key={p.name} type="button" onClick={() => set('style', { ...p.style })} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:border-gray-300">
                          <span className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: p.style.accent ?? 'var(--color-brand-primary, #3262B5)' }} />
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {COLOR_SLOTS.map(({ key, label }) => (
                      <Labeled key={key} label={label}>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            aria-label={`${label} colour`}
                            value={draft.style[key] ?? '#ffffff'}
                            onChange={(e) => set('style', { ...draft.style, [key]: e.target.value })}
                            className="h-9 w-12 rounded border border-gray-200 bg-white"
                          />
                          <input
                            className={inputCls}
                            value={draft.style[key] ?? ''}
                            placeholder="Brand default"
                            onChange={(e) => set('style', { ...draft.style, [key]: e.target.value || undefined })}
                          />
                        </div>
                      </Labeled>
                    ))}
                  </div>
                  <Labeled label="Button text" hint="e.g. Get more information, Book an appraisal.">
                    <input className={inputCls} value={draft.submitLabel} onChange={(e) => set('submitLabel', e.target.value)} placeholder="Submit" />
                  </Labeled>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={draft.hideTitle} onChange={(e) => set('hideTitle', e.target.checked)} />
                    Hide the form's title (when your page already has a heading)
                  </label>
                </div>
              </Card>
            )}

            {tab === 'share' && id && (
              <div className="space-y-4">
                <Card title="Link">
                  <p className="text-sm text-gray-600 mb-2">Send this link or put it behind a button — it opens the form on its own page.</p>
                  <div className="flex gap-2">
                    <input readOnly value={publicUrl ?? ''} onFocus={(e) => e.target.select()} className={cn(inputCls, 'font-mono text-xs')} />
                    <button type="button" onClick={() => copy('link')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm whitespace-nowrap hover:bg-gray-50">
                      {copied === 'link' ? <Check size={15} className="text-success" /> : <Copy size={15} />} {copied === 'link' ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </Card>
                <Card title="Embed on your website">
                  <p className="text-sm text-gray-600 mb-2">
                    Paste this into the page where the form should appear (an HTML / “embed code” block). It sizes itself to the
                    form. Changes you save here show up on your site straight away — no need to paste it again.
                  </p>
                  <textarea readOnly rows={4} value={embedSnippet(id, draft.name)} onFocus={(e) => e.target.select()} className={cn(inputCls, 'font-mono text-xs')} />
                  <button type="button" onClick={() => copy('embed')} className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-dark">
                    {copied === 'embed' ? <Check size={15} /> : <Code size={15} />} {copied === 'embed' ? 'Copied' : 'Copy embed code'}
                  </button>
                </Card>
              </div>
            )}
          </div>

          <aside className="min-w-0 xl:sticky xl:top-6 self-start" aria-label="Preview">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-brand-darkest">Preview</span>
              <div className="flex rounded-lg border border-gray-200 bg-white p-0.5" role="radiogroup" aria-label="Preview width">
                {(
                  [
                    ['desktop', Monitor],
                    ['phone', Smartphone],
                  ] as const
                ).map(([d, Icon]) => (
                  <button
                    key={d}
                    type="button"
                    role="radio"
                    aria-checked={device === d}
                    aria-label={d === 'desktop' ? 'Desktop' : 'Phone'}
                    onClick={() => setDevice(d)}
                    className={cn('p-1.5 rounded-md', device === d ? 'bg-brand-primary/10 text-brand-primary' : 'text-gray-400 hover:text-gray-600')}
                  >
                    <Icon size={15} />
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-[repeating-linear-gradient(45deg,#f3f4f6_0_10px,#eef0f3_10px_20px)] p-4 flex justify-center">
              <div
                className={cn('rounded-xl shadow-sm p-6 transition-[width]', device === 'phone' ? 'w-[360px]' : 'w-full max-w-md')}
                style={{ backgroundColor: draft.style.background ?? '#ffffff' }}
              >
                <FormView
                  form={{ ...draft, name: draft.name || 'Untitled form', fields: cleanFields }}
                  values={previewValues}
                  onChange={(key, v) => setPreviewValues((vs) => ({ ...vs, [key]: v }))}
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault()
                    setPreviewNote(true)
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2" aria-live="polite">
              {previewNote ? 'This is a preview — nothing was sent.' : 'Exactly what visitors see. Try it — nothing is sent from the preview.'}
            </p>
          </aside>
        </div>
      )}
    </PageContainer>
  )
}
